import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Collapse,
  Fade,
  Stack,
  Button,
  IconButton,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  InputBase,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { TransitionGroup } from "react-transition-group";
import { differenceInSeconds } from "date-fns";
import MicRounded from "@mui/icons-material/MicRounded";
import QrCodeScannerRounded from "@mui/icons-material/QrCodeScannerRounded";
import SmartphoneRounded from "@mui/icons-material/SmartphoneRounded";
import PersonRounded from "@mui/icons-material/PersonRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import KeyboardRounded from "@mui/icons-material/KeyboardRounded";
import TranslateRounded from "@mui/icons-material/TranslateRounded";
import HotelRounded from "@mui/icons-material/HotelRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import DirectionsWalkRounded from "@mui/icons-material/DirectionsWalkRounded";
import DescriptionRounded from "@mui/icons-material/DescriptionRounded";
import MedicalServicesRounded from "@mui/icons-material/MedicalServicesRounded";
import EncounterSettingMenu, {
  NOTE_FORMAT_OPTIONS,
  encounterSettingLabel,
  noteFormatLabel,
} from "./EncounterSettingMenu";
import { SPECIALTIES, specialtyLabel } from "./specialties";
import { ENCOUNTER_SETTINGS, isEventsNoteFormat } from "./inpatient";
import HelpOutlineRounded from "@mui/icons-material/HelpOutlineRounded";
import useTranscriptRewrite from "hooks/useTranscriptRewrite";
import PhoneRecordingDialog from "./PhoneRecordingDialog";
import RecordButton from "./common/RecordButton";
import RecordingClock from "./common/RecordingClock";
import TestEncountersPanel from "./TestEncountersPanel";
import ContradictionList from "./ContradictionList";
import { SUPPORTED_LANGUAGES } from "components/dashboard/chartmind/common/LanguageSelector";
import ConsumeShareCodeDialog from "components/dashboard/healthRecordsShare/ConsumeShareCodeDialog";
import SharedRecordSummary from "components/dashboard/healthRecordsShare/SharedRecordSummary";

// Provider-side entry point for scanning a patient's shared record / CareStart handoff.
// Re-enabled for the P4 release: restores the "Import shared record" entry point
// (QR / 7-digit code → redeemHealthRecordShareToken → patient context).
const SHOW_IMPORT_SHARED_RECORD = true;

/**
 * One tappable row under the mic. The icon in the brand colour, the current value and a chevron
 * on the right are what say "this is a button", without a border or a fill.
 */
const OptionRow = ({ icon, label, value, onClick, disabled = false, opensMenu = false, color }) => (
  <ListItemButton
    onClick={onClick}
    disabled={disabled}
    aria-haspopup={opensMenu ? "menu" : undefined}
    sx={{ borderRadius: 2, py: 1.25 }}
  >
    <ListItemIcon sx={{ minWidth: 40, color: color || "primary.main" }}>{icon}</ListItemIcon>
    <ListItemText primary={label} />
    {value && (
      <Typography variant="body2" color="text.secondary" sx={{ ml: 2, whiteSpace: "nowrap" }}>
        {value}
      </Typography>
    )}
    {/* No chevron on a row that cannot be used: it would still look like something to press. */}
    {disabled ? null : opensMenu ? (
      <ExpandMoreRounded fontSize="small" sx={{ ml: 1, color: "text.secondary" }} />
    ) : (
      <ChevronRightRounded fontSize="small" sx={{ ml: 1, color: "text.secondary" }} />
    )}
  </ListItemButton>
);

const RecordingStep = ({
  isSupported,
  isRecording,
  isGeneratingChart,
  transcript,
  error,
  onMicClick,
  onClearError,
  onTranscriptChange,
  language,
  onLanguageChange,
  // Inpatient encounters call the transcript the Narrative.
  transcriptLabel = "Transcript",
  // Seconds recorded on this device, its live mic level (0–1), and speech chunks still being
  // transcribed.
  recordingTime = 0,
  micLevel = 0,
  pendingSegments = 0,
  // True while a pasted transcript is being clarified in the background.
  onPasteClarifyingChange = () => {},
  // Inpatient or outpatient. Omit the change handler to hide the row.
  encounterSetting,
  onEncounterSettingChange,
  // Fixed once the encounter is under way: it picks the prompts and the note, so changing it
  // halfway would leave a differential for one setting and a note for the other.
  encounterSettingLocked = false,
  noteFormat,
  onNoteFormatChange,
  specialty,
  onSpecialtyChange,
  // Events note type: replace the mic with Continue to enter the Event stepper.
  onContinueToEvents = undefined,
  // Typing or pasting instead of recording; owned by the session so it survives leaving the step.
  textModeChosen = false,
  onTextModeChange = () => {},
  clarifyingQuestions = [],
  clarifyingQuestionsLoading = false,
  clarifyingQuestionsEnabled = true,
  onClarifyingQuestionsEnabledChange = () => {},
  // Detected on the same poll as the questions, so they live in the same panel and under the
  // same switch. See ContradictionList.
  contradictions = [],
  onToggleContradictionResolved = () => {},
  showTestEncounters = false,
  testEncounters = [],
  testEncountersLoading = false,
  testEncountersError = "",
  onSelectTestEncounter = () => {},
  onSaveTestEncounter = async () => {},
  onDeleteTestEncounter = async () => {},
  onPatientRecordLoaded,
  importedPatient = null,
  // True when a restored session knows which patient this encounter is for but no longer
  // holds their record, so the AI steps would run without their medications and allergies.
  patientRecordUnavailable = false,
  hideImportRecord = false,
  // Phone microphone relay. `phoneRecording` is true while the PHONE owns the mic, which is a
  // different question from `isRecording` above (this device's own mic) — see captureActive.
  phoneRecording = false,
  phonePairing = false,
  phonePairingId = null,
  phoneGrant = null,
  phoneGrantUnavailable = false,
  phoneConnected = false,
  phonePairingStarting = false,
  phoneError = "",
  phoneClaimedAt = null,
  showRecordOnPhone = false,
  onRecordOnPhone,
  onCancelPhonePairing,
  onStopPhoneRecording,
}) => {
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [recordViewOpen, setRecordViewOpen] = useState(false);
  const transcriptFieldRef = useRef(null);

  // Without voice, typing is the only way forward, so land with the cursor already in the field.
  // preventScroll matters here: plain autoFocus scrolls the field into view, which would push the
  // voice explanation above it off screen and hide the one thing this browser is missing.
  useEffect(() => {
    if (isSupported || isRecording) return;
    transcriptFieldRef.current?.focus({ preventScroll: true });
  }, [isSupported, isRecording]);

  // True while EITHER device is capturing. Drives the copy, the disabled mic and the read-only
  // transcript, because from this screen's point of view an encounter is being recorded either
  // way. `isRecording` alone still means "this device's microphone".
  const capturing = isRecording || phoneRecording;

  // Elapsed time for the phone chip, from the server timestamp the phone wrote when it claimed
  // the pairing. date-fns rather than native Date math, per the project convention.
  // Acknowledges a successful scan on the page rather than in the dialog, per the project's
  // rule that post-mutation success is a dismissible inline Alert. Re-arms whenever a new
  // pairing connects, so a second phone in the same encounter is acknowledged too.
  const [phoneConnectedDismissed, setPhoneConnectedDismissed] = useState(false);
  useEffect(() => {
    if (!phoneConnected) setPhoneConnectedDismissed(false);
  }, [phoneConnected]);

  /**
   * Stopping from the desktop is a round trip, not a local action.
   *
   * It writes `stopping`, the phone flushes its last few seconds and answers `stopped`, and
   * only then does this screen change. Held here rather than in the relay hook because it is
   * purely about this button's feedback, and it clears itself when `phoneRecording` goes
   * false — including the timeout path, where the phone never answers at all and the desktop
   * gives up on its own.
   */
  const [stoppingPhone, setStoppingPhone] = useState(false);
  useEffect(() => {
    if (!phoneRecording) setStoppingPhone(false);
  }, [phoneRecording]);

  const handleStopPhoneRecording = () => {
    setStoppingPhone(true);
    Promise.resolve(onStopPhoneRecording?.()).catch(() => setStoppingPhone(false));
  };

  const [phoneSeconds, setPhoneSeconds] = useState(0);
  useEffect(() => {
    if (!phoneRecording || !phoneClaimedAt) {
      setPhoneSeconds(0);
      return undefined;
    }
    const claimedDate = phoneClaimedAt.toDate ? phoneClaimedAt.toDate() : phoneClaimedAt;
    const tick = () => setPhoneSeconds(Math.max(0, differenceInSeconds(new Date(), claimedDate)));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [phoneRecording, phoneClaimedAt]);

  // Pasted transcripts get a negation-normalizing pass in the background before analysis (see
  // useTranscriptRewrite). While it runs the page holds the next step, which shows the wait.
  const { rewriting, handlePaste } = useTranscriptRewrite({
    transcript,
    onTranscriptChange,
    enabled: true,
    disabled: capturing,
  });
  useEffect(() => {
    onPasteClarifyingChange(rewriting);
  }, [rewriting, onPasteClarifyingChange]);
  // Leaving the step mid-clarify must not leave the rest of the encounter waiting on it.
  useEffect(() => () => onPasteClarifyingChange(false), [onPasteClarifyingChange]);

  // Questions the clinician has waved away for this encounter.
  const [dismissedQuestions, setDismissedQuestions] = useState(() => new Set());
  const visibleQuestions = clarifyingQuestions.filter((q) => !dismissedQuestions.has(q));

  const hasTranscript = Boolean(transcript?.trim());
  const questionsShown =
    isSupported && clarifyingQuestionsEnabled && (visibleQuestions.length > 0 || contradictions.length > 0);

  // Text mode puts the transcript box where the mic was. Without voice it is the only mode.
  const eventsNoteSetup = isEventsNoteFormat(noteFormat) && typeof onContinueToEvents === "function";
  const textMode = !eventsNoteSetup && (!isSupported || textModeChosen);
  // In voice mode the transcript appears under the options once there is something in it.
  const showVoiceTranscript = !textMode && (capturing || hasTranscript);

  // A cleared transcript is a new encounter: bring back any questions dismissed for the last one.
  useEffect(() => {
    if (!hasTranscript) setDismissedQuestions(new Set());
  }, [hasTranscript]);

  const switchToText = () => {
    onTextModeChange(true);
    setTimeout(() => transcriptFieldRef.current?.focus(), 0);
  };

  const [languageAnchor, setLanguageAnchor] = useState(null);
  const [settingAnchor, setSettingAnchor] = useState(null);
  const [noteFormatAnchor, setNoteFormatAnchor] = useState(null);
  const [specialtyAnchor, setSpecialtyAnchor] = useState(null);
  const currentLanguage =
    SUPPORTED_LANGUAGES.find((l) => l.code === (language || "en-US")) || SUPPORTED_LANGUAGES[0];

  const busy = capturing || isGeneratingChart;

  // The transcript, as the chat composer's card without a send button: the footer's Next
  // is what moves the encounter on. The live readout while recording, the input in text mode.
  const transcriptCard = (
    <>
      {/* The same card as the chat composer, without a send button: the footer's
          Next is what moves the encounter on. */}
      <Paper variant="outlined" sx={{ borderRadius: 6, px: 2.5, py: 2, "&:focus-within": { boxShadow: 1 } }}>
        <InputBase
          multiline
          fullWidth
          inputRef={transcriptFieldRef}
          value={transcript}
          onChange={(e) => !capturing && onTranscriptChange?.(e.target.value)}
          onPaste={handlePaste}
          // Read-only while the phone records too, which is what keeps
          // both-devices-editing from being a problem that needs resolving.
          readOnly={capturing}
          placeholder={
            capturing
              ? "Listening…"
              : isSupported
                ? `Type or paste the ${transcriptLabel.toLowerCase()} here`
                : "Type or paste the patient encounter here"
          }
          minRows={isSupported ? 3 : 8}
          maxRows={isSupported ? 12 : 20}
          inputProps={{ "aria-label": transcriptLabel }}
          sx={{ lineHeight: 1.6 }}
        />
      </Paper>
      {/* Speech is transcribed in chunks, so text lands seconds after it is spoken. Saying so
          is what keeps a quiet transcript from reading as a broken one. */}
      {/* The line is always there while recording and only fades, so it never moves the page. */}
      {(capturing || pendingSegments > 0) && (
        <Fade in={pendingSegments > 0}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, px: 1 }}>
            <CircularProgress size={12} />
            <Typography variant="caption" color="text.secondary">
              Transcribing…
            </Typography>
          </Stack>
        </Fade>
      )}
    </>
  );


  return (
    // A fixed offset from the top rather than true vertical centring (as the Medical Library's
    // empty state does): centring recentred the page every time something appeared below the
    // mic, which moved the mic. With an offset, new content only grows downward.
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        pt: { xs: 2, md: "10vh" },
      }}
    >
      {/* Laid out like the Medical Library's empty state: the mic where its icon sits, a title,
          one plain sentence, the choice that shapes the encounter, then the other ways in as
          the same soft starter buttons the chat pages use. */}
      <Stack alignItems="center" sx={{ width: "100%", maxWidth: 600, textAlign: "center" }}>
        <Typography variant="h4" sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: "1.75rem", sm: "2.125rem" } }}>
          {phoneRecording ? "Recording on your phone" : isRecording ? "Listening…" : "ChartMind"}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
          {eventsNoteSetup
            ? "Set specialty and visit details, then continue to document the event."
            : phoneRecording
              ? "The transcript appears below as you talk."
              : isRecording
                ? "Tap the mic again when the visit is done."
                : textMode
                  ? "Type or paste the visit and get a differential, a plan and a finished note."
                  : "Record the visit and get a differential, a plan and a finished note."}
        </Typography>

        {eventsNoteSetup ? (
          <Stack alignItems="center" spacing={1.5} sx={{ mb: 5 }}>
            <RecordButton
              variant="continue"
              onClick={onContinueToEvents}
              ariaLabel="Continue to Event"
            />
            <Typography variant="button" color="text.secondary">
              Continue
            </Typography>
          </Stack>
        ) : textMode ? (
          <Box sx={{ width: "100%", maxWidth: 560, mb: 5, textAlign: "left" }}>{transcriptCard}</Box>
        ) : (
          <Box sx={{ mb: 5 }}>
            {/* One button and one clock however the encounter is being recorded. While the phone
                records, this is its stop button: stopping waits on the phone to flush and answer,
                so the spinner inside it is not cosmetic. */}
            {phoneRecording ? (
              <RecordButton
                recording
                busy={stoppingPhone}
                onClick={handleStopPhoneRecording}
                ariaLabel="Stop recording on your phone"
              />
            ) : (
              <RecordButton
                recording={isRecording}
                busy={isGeneratingChart}
                onClick={onMicClick}
                level={isRecording ? micLevel : undefined}
                ariaLabel={isRecording ? "Stop recording" : "Start recording"}
              />
            )}
            {(isRecording || phoneRecording) && (
              <RecordingClock seconds={phoneRecording ? phoneSeconds : recordingTime} sx={{ mt: 3 }} />
            )}
          </Box>
        )}

        {onEncounterSettingChange && (
          <EncounterSettingMenu
            anchorEl={settingAnchor}
            onClose={() => setSettingAnchor(null)}
            value={encounterSetting}
            onChange={onEncounterSettingChange}
          />
        )}
        {onSpecialtyChange && (
          <EncounterSettingMenu
            anchorEl={specialtyAnchor}
            onClose={() => setSpecialtyAnchor(null)}
            value={specialty}
            onChange={onSpecialtyChange}
            options={SPECIALTIES}
          />
        )}
        {onNoteFormatChange && (
          <EncounterSettingMenu
            anchorEl={noteFormatAnchor}
            onClose={() => setNoteFormatAnchor(null)}
            value={noteFormat}
            onChange={onNoteFormatChange}
            options={NOTE_FORMAT_OPTIONS}
          />
        )}
        <Menu
          anchorEl={languageAnchor}
          open={Boolean(languageAnchor)}
          onClose={() => setLanguageAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {SUPPORTED_LANGUAGES.map((lang) => (
            <MenuItem
              key={lang.code}
              selected={lang.code === currentLanguage.code}
              onClick={() => {
                onLanguageChange(lang.code);
                setLanguageAnchor(null);
              }}
            >
              {lang.flag}&nbsp;&nbsp;{lang.label}
            </MenuItem>
          ))}
        </Menu>

        {error && (
          <Alert severity="error" onClose={onClearError} sx={{ width: "100%", mt: 3, textAlign: "left" }}>
            {error}
          </Alert>
        )}
      </Stack>

      {phoneConnected && !phoneConnectedDismissed && (
        <Alert severity="success" onClose={() => setPhoneConnectedDismissed(true)} sx={{ width: "100%", maxWidth: 560, mb: 3 }}>
          Please tap the microphone on your phone to start recording.
        </Alert>
      )}

      {/* Restored sessions keep the patient's identity but not their record. Say so, rather
          than letting every AI step quietly run without their medications and allergies. */}
      {SHOW_IMPORT_SHARED_RECORD && !hideImportRecord && patientRecordUnavailable && (
        <Alert
          severity="warning"
          sx={{ width: "100%", maxWidth: 560, mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => setShareDialogOpen(true)} disabled={busy}>
              Import record
            </Button>
          }
        >
          This encounter is linked to a patient, but their record is not loaded, so each step
          will run without their medications and allergies. Please import the record again.
        </Alert>
      )}

      {SHOW_IMPORT_SHARED_RECORD && !hideImportRecord && (
        <ConsumeShareCodeDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          onRecordLoaded={(record) => {
            onPatientRecordLoaded?.(record);
          }}
        />
      )}

      {/* Re-open the imported record. Same component the import dialog and the public /share
          page render, so the record reads identically wherever a provider meets it. */}
      <Dialog open={recordViewOpen} onClose={() => setRecordViewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Patient record</DialogTitle>
        <DialogContent dividers>
          <SharedRecordSummary result={importedPatient} />
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            onClick={() => {
              setRecordViewOpen(false);
              onPatientRecordLoaded?.(null);
            }}
            sx={{ mr: 'auto' }}
          >
            Remove from encounter
          </Button>
          <Button variant="contained" onClick={() => setRecordViewOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clarifying questions: they wait in the background and appear only once there is
          something to ask. First under the mic while recording, because they are what the
          clinician acts on mid-visit. Each can be waved away. */}
      {/* Slides open, and each question slides in, rather than popping in and shoving the
          transcript below it down. */}
      <Collapse
        in={questionsShown}
        unmountOnExit
        sx={{ width: "100%", maxWidth: 560 }}
      >
        <Box sx={{ width: "100%", pt: 4 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Clarifying questions
          </Typography>

          {clarifyingQuestionsEnabled && (
            <Stack spacing={1} alignItems="flex-start">
              {/* Above the questions, because a contradiction is usually the reason for the
                  question below it. */}
              {contradictions.length > 0 && (
                <Box sx={{ width: "100%" }}>
                  <ContradictionList
                    contradictions={contradictions}
                    onToggleResolved={onToggleContradictionResolved}
                  />
                </Box>
              )}
              <List dense disablePadding sx={{ width: "100%" }}>
                <TransitionGroup>
                {visibleQuestions.map((question) => (
                  <Collapse key={question}>
                  <ListItem
                    disableGutters
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        aria-label="Dismiss question"
                        onClick={() => setDismissedQuestions((prev) => new Set(prev).add(question))}
                      >
                        <CloseRounded fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: "primary.main" }}>
                      <HelpOutlineRounded fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={question} primaryTypographyProps={{ variant: "body1" }} />
                  </ListItem>
                  </Collapse>
                ))}
                </TransitionGroup>
              </List>
            </Stack>
          )}
        </Box>
      </Collapse>

      {showVoiceTranscript && <Box sx={{ width: "100%", maxWidth: 560, mt: 4 }}>{transcriptCard}</Box>}

      {/* Everything else, as tappable rows. Below the questions and the transcript, so stopping
          a recording never pushes them down; right under the mic on an empty encounter. Gone
          while recording: every row is locked then. */}
      {!capturing && (
        <Stack
          spacing={0.5}
          sx={{ width: "100%", maxWidth: 560, textAlign: "left", mt: showVoiceTranscript || questionsShown ? 4 : 0 }}
        >
          {onSpecialtyChange && (
            <OptionRow
              icon={<MedicalServicesRounded />}
              label="Specialty"
              value={specialtyLabel(specialty)}
              onClick={(e) => setSpecialtyAnchor(e.currentTarget)}
              disabled={busy || encounterSettingLocked}
              opensMenu
            />
          )}
          {onEncounterSettingChange && (
            <OptionRow
              icon={encounterSetting === ENCOUNTER_SETTINGS.INPATIENT ? <HotelRounded /> : <DirectionsWalkRounded />}
              label="Visit type"
              value={encounterSettingLabel(encounterSetting)}
              onClick={(e) => setSettingAnchor(e.currentTarget)}
              disabled={busy || encounterSettingLocked}
              opensMenu
            />
          )}
          {onNoteFormatChange && encounterSetting === ENCOUNTER_SETTINGS.INPATIENT && (
            <OptionRow
              icon={<DescriptionRounded />}
              label="Note type"
              value={noteFormatLabel(noteFormat)}
              onClick={(e) => setNoteFormatAnchor(e.currentTarget)}
              disabled={busy || encounterSettingLocked}
              opensMenu
            />
          )}
          {/* Speech-recognition locale for this mic AND the phone pairing. */}
          {!eventsNoteSetup && isSupported && !textMode && (
            <OptionRow
              icon={<TranslateRounded />}
              label="Language"
              value={currentLanguage.label}
              onClick={(e) => setLanguageAnchor(e.currentTarget)}
              disabled={busy}
              opensMenu
            />
          )}
          {/* Not gated on `isSupported`: a browser without voice is exactly where the phone helps.
              ⚠️ "Phone connected" STAYS CLICKABLE — it is the only way to start the next encounter
              on the same phone (handleRecordOnPhone re-arms the pairing). */}
          {!eventsNoteSetup && showRecordOnPhone && (
            <OptionRow
              icon={<SmartphoneRounded />}
              label={phoneConnected ? "Phone connected" : "Record on your phone"}
              value={phoneConnected ? "Start new" : null}
              onClick={() => {
                setPhoneConnectedDismissed(false);
                onRecordOnPhone?.();
              }}
              disabled={busy}
            />
          )}
          {SHOW_IMPORT_SHARED_RECORD && !hideImportRecord && (
            <OptionRow
              icon={importedPatient ? <PersonRounded /> : <QrCodeScannerRounded />}
              color={patientRecordUnavailable ? "warning.main" : undefined}
              label={
                !importedPatient
                  ? "Import the patient's record"
                  : patientRecordUnavailable
                    ? "Record not loaded, import again"
                    : importedPatient.patientName
                      ? `View ${importedPatient.patientName}'s record`
                      : "View the imported record"
              }
              onClick={() =>
                importedPatient && !patientRecordUnavailable
                  ? setRecordViewOpen(true)
                  : setShareDialogOpen(true)
              }
              disabled={busy && !(importedPatient && !patientRecordUnavailable)}
            />
          )}
          {/* Voice and text swap places: the mic or the transcript box, never both at the top.
              Hidden in voice mode once the transcript is showing under the mic: that box is
              already editable, so switching would only rearrange the page. */}
          {!eventsNoteSetup && isSupported && !showVoiceTranscript && (
            <OptionRow
              icon={textMode ? <MicRounded /> : <KeyboardRounded />}
              label={textMode ? "Switch to voice" : "Type or paste instead"}
              onClick={textMode ? () => onTextModeChange(false) : switchToText}
              disabled={busy}
            />
          )}
        </Stack>
      )}


      {showRecordOnPhone && (
        <PhoneRecordingDialog
          open={phonePairing}
          onClose={onCancelPhonePairing}
          pairingId={phonePairingId}
          grant={phoneGrant}
          grantUnavailable={phoneGrantUnavailable}
          starting={phonePairingStarting}
          error={phoneError}
          language={language || "en-US"}
          onRetry={onRecordOnPhone}
        />
      )}

      {showTestEncounters ? (
        <TestEncountersPanel
          encounters={testEncounters}
          loading={testEncountersLoading}
          error={testEncountersError}
          disabled={capturing || isGeneratingChart}
          onSelectEncounter={onSelectTestEncounter}
          onSaveEncounter={onSaveTestEncounter}
          onDeleteEncounter={onDeleteTestEncounter}
        />
      ) : null}
    </Box>
  );
};

export default RecordingStep;
